import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Slider,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { StyledCreateInvoice } from "./CreateInvoice.styled";
import { Controller } from "react-hook-form";
import { useProject } from "../../stores/useProject";
import { useEffect, useState } from "react";
import { Project } from "../../models/Project";

import {
  InvoiceCreateDto,
  InvoiceItemDto,
} from "../../models/dto/CreateInvoiceDto";
import { addDays } from "date-fns";
import { useInvoice } from "../../stores/useInvoice";
import { CreateInvoiceDataInput } from "../createInvoiceDataInput/createInvoiceDataInput";
import { CreateInvoiceForm } from "../createInvoiceForm/CreateInvoiceForm";
import {
  ArchiveIcon,
  ArrowUDownLeftIcon,
  CheckIcon,
  FileCloudIcon,
  GlobeHemisphereWestIcon,
  TrashSimpleIcon,
  UploadIcon,
} from "@phosphor-icons/react";
import { useProfile } from "../../stores/useProfile";
import { useNavigate } from "react-router-dom";
import { useAsyncStatus } from "../../hooks/useAsyncStatus";

export const CreateInvoice = () => {
  const invoiceDraft = localStorage.getItem("invoiceDraft");
  const { invoices, createInvoice, fetchInvoices } = useInvoice();
  const { user } = useProfile();
  const { run, waitingNode } = useAsyncStatus();
  const [dataInputView, setDataInputView] = useState<boolean>(true);
  const [confirmation, setConfirmation] = useState<boolean>(false);
  const [invoiceData, setInvoiceData] = useState<
    Partial<InvoiceCreateDto & { project: Project | null }>
  >(() => {
    if (invoiceDraft) {
      const parsed = JSON.parse(invoiceDraft);
      return {
        ...parsed,
        invoiceDate: parsed.invoiceDate
          ? new Date(parsed.invoiceDate)
          : new Date(),
        invoiceDeliveryDate: parsed.invoiceDeliveryDate
          ? new Date(parsed.invoiceDeliveryDate)
          : new Date(),
        invoiceDueDate: parsed.invoiceDueDate
          ? new Date(parsed.invoiceDueDate)
          : new Date(),
      };
    }

    return {
      project: null,
      invoiceDate: new Date(),
      invoiceDeliveryDate: new Date(),
      invoiceDueDate: addDays(new Date(), 15),
      vat: 6,
      contactPeppolId: "",
      priceBefore: 0,
      priceVat: 0,
      invoiceItems: [],
      sentWithPeppol: false,
    };
  });
  const navigate = useNavigate();

  const GoToTextPage = () => {
    const serial = (
      (invoices?.filter(
        (i) =>
          i.type === "inkomst" &&
          i.name !== "" &&
          new Date(i.invoiceDate).getFullYear() === new Date().getFullYear()
      ).length || 0) + 1
    )
      .toString()
      .padStart(2, "0");

    const year = invoiceData.invoiceDate?.getFullYear();
    const month = (invoiceData.invoiceDate!.getMonth() + 1)
      .toString()
      .padStart(2, "0");

    setInvoiceData({
      ...invoiceData,
      contactAddressCity: invoiceData.project?.contact.address.city || "",
      contactAddressNumber: invoiceData.project?.contact.address.number || "",
      contactAddressStreet: invoiceData.project?.contact.address.street || "",
      contactAddressZipcode: invoiceData.project?.contact.address.zipCode || "",
      contactCompanyName: invoiceData.project?.contact.company || "",
      contactVatNumber: invoiceData.project?.contact.vat || "",
      contactFirstName: invoiceData.project?.contact.firstName || "",
      contactLastName: invoiceData.project?.contact.lastName || "",
      contactMail: invoiceData.project?.contact.email || "",
      contactPhone: invoiceData.project?.contact.phone || "",
      contactPeppolId: invoiceData.project?.contact.peppolId || "",
      invoiceCode: getNextInvoiceCode(),
    });

    setDataInputView(false);
  };

  const handleRemove = (invoiceItem: InvoiceItemDto) => {
    setInvoiceData((prev) => {
      const filtered = prev.invoiceItems!.filter(
        (item) => item !== invoiceItem
      );

      const reindexed = filtered.map((item, index) => ({
        ...item,
        position: index + 1,
      }));

      return {
        ...prev,
        invoiceItems: reindexed,
      };
    });
  };

  const handleEditInvoiceItem = (
    key: string,
    value: string | number,
    position: number
  ) => {
    setInvoiceData((prev) => ({
      ...prev,
      invoiceItems: prev.invoiceItems?.map((item) => {
        if (item.position !== position) return item;

        const updatedItem = { ...item, [key]: value };
        const unitprice = Number(updatedItem.unitprice ?? 0);
        const amount = Number(updatedItem.amount ?? 0);
        const vat = Number(updatedItem.vat ?? 0);

        updatedItem.priceBefore = unitprice * amount;
        updatedItem.priceTotal = unitprice * amount * (vat / 100 + 1);

        return updatedItem;
      }),
    }));
  };

  useEffect(() => {
    if (!invoiceData.project) return;

    setInvoiceData((prev) => {
      if (!prev.project) return prev;

      const contact = prev.project.contact;
      const address = contact.address;

      return {
        ...prev,
        contactAddressCity: address.city,
        contactAddressNumber: address.number,
        contactAddressStreet: address.street,
        contactAddressZipcode: address.zipCode,
        contactCompanyName: contact.company,
        contactFirstName: contact.firstName,
        contactLastName: contact.lastName,
        contactPhone: contact.phone,
        contactMail: contact.email,
        contactPeppolId: contact.peppolId,
        sentWithPeppol: contact.peppolId ? true : false,
        contactVatNumber: contact.vat?.replaceAll(" ", "").replaceAll(".", ""),
        projectId: prev.project.id,
      };
    });
  }, [invoiceData.project]);

  useEffect(() => {
    if (!invoiceData.project) return;

    setInvoiceData((prev) => {
      return {
        ...prev,
        priceBefore: invoiceData.invoiceItems!.reduce(
          (sum, item) => sum + item.priceBefore,
          0
        ),
        priceVat: Number(
          invoiceData.invoiceItems!.reduce(
            (sum, item) => sum + item.priceTotal,
            0
          ) -
            invoiceData.invoiceItems!.reduce(
              (sum, item) => sum + item.priceBefore,
              0
            )
        ),
      };
    });
  }, [invoiceData.invoiceItems]);

  const clearDraft = () => {
    setInvoiceData({
      project: null,
      invoiceDate: new Date(),
      invoiceDeliveryDate: new Date(),
      invoiceDueDate: addDays(new Date(), 15),
      vat: 6,
      invoiceItems: [],
      sentWithPeppol: false,
    });
    localStorage.removeItem("invoiceDraft");
    setDataInputView(true);
    setConfirmation(false);
  };

  const onUpload = async () => {
    const { project, ...invoiceWithoutProject } = invoiceData ?? {};

    if (!user) return;
    const response = await run(() =>
      createInvoice(user, invoiceWithoutProject as InvoiceCreateDto)
    );

    if (response) {
      clearDraft();
      fetchInvoices(user);
      navigate("/invoices");
    }
  };

  const getNextInvoiceCode = () => {
  if (!invoices) return "ERROR";

  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");

  const searchPrefix = `FAC-${year}`;      // enkel jaar -> serial per jaar
  const outputPrefix = `FAC-${year}${month}`; // jaar + maand voor weergave

  const usedSerials = invoices
    .filter((i) => i.type === "inkomst")
    .map((i) => i.name)
    .filter((name) => name.startsWith(searchPrefix))
    .map((name) => Number(name.split("-")[2]))
    .filter((n) => !isNaN(n))
    .sort((a, b) => a - b);

  let nextSerial = 1;
  for (const serial of usedSerials) {
    if (serial !== nextSerial) break;
    nextSerial++;
  }

  const serialStr = String(nextSerial).padStart(2, "0");

  return `${outputPrefix}-${serialStr}-${invoiceData.project?.code}`;
};

  return (
    <StyledCreateInvoice>
      {waitingNode}
      {dataInputView ? (
        <CreateInvoiceDataInput
          onClick={GoToTextPage}
          onChange={(val) => setInvoiceData(val)}
          invoiceData={invoiceData}
        />
      ) : (
        <Box className="invoiceFormWrapper">
          <Box className="invoiceSettings">
            <Tooltip title="Opslaan als draft">
              <Button
                onClick={() =>
                  localStorage.setItem(
                    "invoiceDraft",
                    JSON.stringify(invoiceData)
                  )
                }>
                <ArchiveIcon size={40} weight="bold" />
              </Button>
            </Tooltip>
            {confirmation ? (
              <Tooltip title="Factuur verwijderen bevestigen">
                <Button onClick={clearDraft}>
                  <CheckIcon size={40} weight="bold" />
                </Button>
              </Tooltip>
            ) : (
              <Tooltip title="Factuur Verwijderen">
                <Button onClick={() => setConfirmation(true)}>
                  <TrashSimpleIcon size={40} weight="bold" />
                </Button>
              </Tooltip>
            )}
            <Tooltip title="Verander taal (soon)">
              <Button>
                <GlobeHemisphereWestIcon size={40} weight="bold" />
              </Button>
            </Tooltip>
            <Tooltip title="Verstuur met peppol">
              <Button
                className={`toggleButton ${
                  invoiceData.sentWithPeppol ? "toggled" : ""
                }`}
                onClick={() =>
                  setInvoiceData({
                    ...invoiceData,
                    sentWithPeppol: !invoiceData.sentWithPeppol,
                  })
                }>
                <FileCloudIcon size={40} weight="bold" />
              </Button>
            </Tooltip>
            <Tooltip title="Opslaan / versturen">
              <Button onClick={onUpload}>
                <UploadIcon size={40} weight="bold" />
              </Button>
            </Tooltip>
            <Tooltip title="Terug naar instellingen">
              <Button onClick={() => setDataInputView(true)}>
                <ArrowUDownLeftIcon size={40} weight="bold" />
              </Button>
            </Tooltip>
          </Box>
          <CreateInvoiceForm
            invoiceData={invoiceData}
            onSave={(value) =>
              setInvoiceData({
                ...invoiceData,
                invoiceItems: [...invoiceData.invoiceItems!, value],
              })
            }
            onRemove={handleRemove}
            onEditItem={handleEditInvoiceItem}
          />{" "}
        </Box>
      )}
    </StyledCreateInvoice>
  );
};
